import { useTu } from "@src/appV2/i18n/useTu";
import { Alert, Box, Divider, Stack } from "@mui/material";
import { useIsMobile } from "@src/appV2/redesign/AppLayout/useResponsive";
import { Text } from "@src/appV2/redesign/components/Text";
import { NotificationSelectorDropdown } from "./NotificationSelectorDropdown";
import { type NotificationChangeArguments, type NotificationRow } from "./types";
const DESKTOP_COLUMN_TEMPLATE = "minmax(0, 1fr) 80px 80px 80px";
const CHANNELS = ["email", "sms", "push"] as const;

interface NotificationPreferencesListProps {
  rows: NotificationRow[];
  smsDisabledNoPhone: boolean;
  onChangeNotification: (arguments_: NotificationChangeArguments) => void;
  onSelectorOpen: () => void;
}

function ColumnHeader(props: { label: string }) {
  return (
    <Text
      semibold
      variant="body2"
      sx={(theme) => ({ color: theme.palette.text?.primary, textAlign: "center" })}
    >
      {props.label}
    </Text>
  );
}

interface RowProps {
  row: NotificationRow;
  onChangeNotification: (arguments_: NotificationChangeArguments) => void;
  onSelectorOpen: () => void;
}

function DesktopRow(props: RowProps) {
  const { row, onChangeNotification, onSelectorOpen } = props;

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: DESKTOP_COLUMN_TEMPLATE,
        alignItems: "center",
        columnGap: 2,
        minHeight: 56,
      }}
    >
      <Text variant="body2">{row.description}</Text>
      {CHANNELS.map((channel) => (
        <Box key={channel} sx={{ display: "flex", justifyContent: "center" }}>
          <NotificationSelectorDropdown
            cell={row.cells[channel]}
            onChange={onChangeNotification}
            onOpen={onSelectorOpen}
          />
        </Box>
      ))}
    </Box>
  );
}

function MobileRow(props: RowProps) {
  const { tu } = useTu("workplaceUsers");
  const { row, onChangeNotification, onSelectorOpen } = props;
  const channelLabels = {
    email: tu("Email"),
    sms: "SMS",
    push: tu("Push"),
  } as const;

  return (
    <Stack
      spacing={2}
      sx={(theme) => ({
        paddingY: theme.spacing(2),
      })}
    >
      <Text semibold variant="body2">
        {row.description}
      </Text>
      <Stack spacing={1}>
        {CHANNELS.map((channel) => (
          <Box
            key={channel}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Text variant="body2" sx={(theme) => ({ color: theme.palette.text?.secondary })}>
              {channelLabels[channel]}
            </Text>
            <NotificationSelectorDropdown
              cell={row.cells[channel]}
              onChange={onChangeNotification}
              onOpen={onSelectorOpen}
            />
          </Box>
        ))}
      </Stack>
    </Stack>
  );
}

export function NotificationPreferencesList(props: NotificationPreferencesListProps) {
  const { tu } = useTu("workplaceUsers");

  const { rows, smsDisabledNoPhone, onChangeNotification, onSelectorOpen } = props;
  const isMobile = useIsMobile();

  return (
    <Stack spacing={3}>
      {smsDisabledNoPhone && (
        <Alert severity="warning">
          {tu(
            "SMS notifications are disabled because no mobile number is set. Add a mobile number in Personal Information to enable SMS notifications."
          )}
        </Alert>
      )}
      {!isMobile && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: DESKTOP_COLUMN_TEMPLATE,
            alignItems: "center",
            columnGap: 2,
          }}
        >
          <Box />
          <ColumnHeader label={tu("Email")} />
          <ColumnHeader label="SMS" />
          <ColumnHeader label={tu("Push")} />
        </Box>
      )}
      <Stack divider={<Divider flexItem />} spacing={0}>
        {rows.map((row) =>
          isMobile ? (
            <MobileRow
              key={row.rowKey}
              row={row}
              onChangeNotification={onChangeNotification}
              onSelectorOpen={onSelectorOpen}
            />
          ) : (
            <DesktopRow
              key={row.rowKey}
              row={row}
              onChangeNotification={onChangeNotification}
              onSelectorOpen={onSelectorOpen}
            />
          )
        )}
      </Stack>
    </Stack>
  );
}
